import React, { useState, useEffect, useCallback, useMemo, Suspense, lazy, useRef } from 'react';
import CHAT_CONFIG, { isFeatureEnabled } from './config/chatConfig';
import { motion, AnimatePresence } from 'framer-motion';
import { ConversationTabBar, SortOption, ReplyRangeDays } from './components/ConversationTabBar';
import FullConversationCard from './components/FullConversationCard';
import { IntegratedConversationItem } from './components/IntegratedConversationItem';
import AnalyticsApiService, { PaginatedConversations, PageParams } from './services/analyticsApi';
import { useInfiniteScroll } from './hooks/useInfiniteScroll';
import { BackendConnectionTest } from './components/BackendConnectionTest';
import { ProfileImageCache } from './services/profileImageCache';
import { conversationCache } from './services/conversationCache';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { LoginPage } from './components/LoginPage';
import { processConversation, processParticipant } from './utils/profileImage';
import { debounce } from './utils/requestQueue';
import { formatVectorDBTimestamp, formatVectorDBTimestampWithDate, parseVectorDbUtc } from './utils/timeZone';
import { Toaster } from 'sonner';
import { transformBackendConversation as transformConvShared } from './utils/conversationTransform';
import { getPrimaryConversationUserId } from './utils/conversationRecipients';
import { foldReactionMessages, type ReactionKind } from './utils/messageReactions';


// Analytics components
import { AllMetricsGrid } from './components/analytics/AllMetricsGrid';
import SegregationPage from './components/segregation/SegregationPage';
import { SeriesDataAgent } from './components/SeriesDataAgent';
import { EnterpriseEngagements } from './components/EnterpriseEngagements';
import ExpandedConversationModal from './components/ExpandedConversationModal';
import { AdminUserManagement } from './components/admin/AdminUserManagement';
import { SettingsPage } from './components/SettingsPage';
import { FlaggedUsersPage } from './components/FlaggedUsersPage';
import { SearchPage } from './components/SearchPage';
import { UserProfilesFeedPage } from './components/UserProfilesFeedPage';
import { canManageUsers } from './utils/permissions';

// Layout components
import { NavigationButton } from './components/layout/NavigationButton';
import { SidebarNavigation } from './components/layout/SidebarNavigation';
import { SETTINGS_ITEM, THEME_ICONS, LOGOUT_ICON } from './config/navigationConfig';

// Export interfaces for use in components
export interface Participant {
  id: string;
  name: string;
  fname?: string;
  lname?: string;
  phoneNumber: string;
  profileImage?: string;
  username?: string;
  role?: string;
  lastActive?: Date;
}

interface Message {
  id: string;
  text: string;
  timestamp: string;
  isOwn: boolean;
  createdAt: Date;
  messageType?: string;
  // Group chat support: identify sender for labeling and dedupe
  sender?: string;
  senderId?: string;
  senderName?: string;
  messageHandle?: string;
  runId?: string;
  metadata?: Record<string, any>;
  deliveryStatus?: 'sending' | 'sent' | 'failed';
  deliveryError?: string;
}

interface Conversation {
  id: string;
  name: string;
  phoneNumber: string; // AI friend's phone number
  userPhoneNumber: string; // Real user's phone number
  profileImage?: string;
  username?: string; // For profile link
  messages: Message[];
  isGroup: boolean;
  lastMessageTime: Date;
  lastUserMessageTime?: Date;
  participants?: Participant[]; // Includes all participants (human + AI) for all conversations
  unread?: boolean;
  lastReadAt?: Date | null;
}

// Mock data with proper types
const mockConversations: Conversation[] = [
  {
    id: '1',
    name: 'Mira (AI Friend)',
    phoneNumber: '(949) 828-2923',
    userPhoneNumber: '(555) 987-1234',
    isGroup: false,
    lastMessageTime: new Date('2024-01-15T12:36:00'),

    messages: [
      {
        id: '1',
        text: "Let's get lunch. How about pizza?",
        timestamp: '12:30 PM',
        isOwn: true,
        createdAt: new Date('2024-01-15T12:30:00')
      },
      {
        id: '2',
        text: "Let's do it! I'm in a meeting until noon.",
        timestamp: '12:32 PM',
        isOwn: false,
        createdAt: new Date('2024-01-15T12:32:00')
      },
      {
        id: '3',
        text: "That's perfect. There's a new place on Main St I've been wanting to check out. I hear their hawaiian pizza is off the hook!",
        timestamp: '12:35 PM',
        isOwn: true,
        createdAt: new Date('2024-01-15T12:35:00')
      },
      {
        id: '4',
        text: "Sounds great! See you at 12:30.",
        timestamp: '12:36 PM',
        isOwn: false,
        createdAt: new Date('2024-01-15T12:36:00')
      }
    ]
  },
  {
    id: '2',
    name: 'Marketing Team',
    phoneNumber: '(555) 345-6789',
    userPhoneNumber: '(555) 987-1234',
    isGroup: true,
    lastMessageTime: new Date('2024-01-17T10:45:00'),

    participants: [
      {
        id: '1',
        name: 'Lisa Chang',
        phoneNumber: '(555) 601-4567',
        role: 'Marketing Director',
        lastActive: new Date('2024-01-17T10:30:00')
      },
      {
        id: '2',
        name: 'David Park',
        phoneNumber: '(555) 928-3744',
        role: 'Brand Manager',
        lastActive: new Date('2024-01-17T09:15:00')
      },
      {
        id: '3',
        name: 'Sarah Kim',
        phoneNumber: '(555) 839-2847',
        role: 'Content Strategist',
        lastActive: new Date('2024-01-17T10:45:00')
      },
      {
        id: '4',
        name: 'James Wilson',
        phoneNumber: '(555) 472-9384',
        role: 'SEO Specialist',
        lastActive: new Date('2024-01-16T16:20:00')
      }
    ],
    messages: [
      {
        id: '1',
        text: "Campaign results are looking great!",
        timestamp: '10:42 AM',
        isOwn: false,
        createdAt: new Date('2024-01-17T10:42:00')
      },
      {
        id: '2',
        text: "The engagement rates exceeded our expectations by 25%",
        timestamp: '10:43 AM',
        isOwn: false,
        createdAt: new Date('2024-01-17T10:43:00')
      }
    ]
  },
  {
    id: '3',
    name: 'CTO',
    phoneNumber: '(555) 701-5001',
    userPhoneNumber: '(555) 987-1234',
    isGroup: false,
    lastMessageTime: new Date('2024-01-16T18:20:00'),

    messages: [
      {
        id: '1',
        text: "Great! I'll prepare the performance metrics dashboard.",
        timestamp: '11:20 AM',
        isOwn: true,
        createdAt: new Date('2024-01-16T11:20:00')
      }
    ]
  },
  {
    id: '4',
    name: 'Design Team',
    phoneNumber: '(555) 987-6543',
    userPhoneNumber: '(555) 987-1234',
    isGroup: true,
    lastMessageTime: new Date('2024-01-16T10:47:00'),

    participants: [
      {
        id: '1',
        name: 'Emma Chen',
        phoneNumber: '(555) 401-2345',
        role: 'Lead Designer',
        lastActive: new Date('2024-01-16T10:30:00')
      },
      {
        id: '2',
        name: 'Alex Rivera',
        phoneNumber: '(555) 283-7456',
        role: 'UX Designer',
        lastActive: new Date('2024-01-16T09:45:00')
      },
      {
        id: '3',
        name: 'Jordan Lee',
        phoneNumber: '(555) 567-8901',
        role: 'UI Designer',
        lastActive: new Date('2024-01-16T10:47:00')
      }
    ],
    messages: [
      {
        id: '1',
        text: "The new mockups are ready for review.",
        timestamp: '10:45 AM',
        isOwn: false,
        createdAt: new Date('2024-01-16T10:45:00')
      },
      {
        id: '2',
        text: "Looks fantastic! Great work everyone.",
        timestamp: '10:46 AM',
        isOwn: true,
        createdAt: new Date('2024-01-16T10:46:00')
      },
      {
        id: '3',
        text: "The color palette really works well with the new layout.",
        timestamp: '10:47 AM',
        isOwn: false,
        createdAt: new Date('2024-01-16T10:47:00')
      }
    ]
  }
];

// Sidebar Component with Enhanced Glass Morphism
function Sidebar() {
  return (
    <div className="relative h-full w-full" data-name="sidebar">
      {/* Base glass layer with multi-gradient lighting */}
      <div className="absolute inset-0 rounded-r-3xl glass-sidebar transition-all duration-300 ease-out" />
      
      {/* Edge highlight for dimensional depth */}
      <div className="absolute right-0 top-[5%] bottom-[5%] w-[1px] glass-edge-highlight opacity-60 pointer-events-none" />
      
      {/* Subtle noise texture overlay to prevent color banding */}
      <div className="absolute inset-0 glass-noise-overlay rounded-r-3xl" aria-hidden="true" />
      
      {/* Ambient light reflection (top) */}
      <div className="absolute top-0 left-0 right-0 h-[30%] bg-gradient-to-b from-white/[0.08] to-transparent rounded-tr-3xl pointer-events-none" aria-hidden="true" />
    </div>
  );
}

// Conversations Page Component
function ConversationsPageContent({ onSearchPageOpen }: { onSearchPageOpen?: () => void }) {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const currentPageRef = useRef(1);
  const authorCursorRef = useRef<string | null>(null);
  const replyCountCursorRef = useRef<string | null>(null);
  const eventUserIdsRef = useRef<string[]>([]);
  const eventUserOffsetRef = useRef(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortOption, setSortOption] = useState<SortOption>('new-old');
  const [replyRangeDays, setReplyRangeDays] = useState<ReplyRangeDays>(7);
  const sortOptionRef = useRef<SortOption>(sortOption);
  const replyRangeDaysRef = useRef<ReplyRangeDays>(replyRangeDays);
  const [activeConversationId, setActiveConversationId] = useState<number | null>(null);
  const [expandedConversationId, setExpandedConversationId] = useState<string | null>(null);
  const [totalConversationCount, setTotalConversationCount] = useState<number | null>(null);
  const [fullHistoryConversations, setFullHistoryConversations] = useState<Set<string>>(new Set());
  const loadedConversationIds = useRef<Set<number>>(new Set());
  const isRestoringRef = useRef(false);
  const lastViewedConversationIdRef = useRef<number | null>(null);
  const loadGenerationRef = useRef(0);
  const pendingFilterReloadRef = useRef(false);
  // Interaction flags
  const [isTyping, setIsTyping] = useState(false);
  const [isInspecting, setIsInspecting] = useState(false);
  const listRefreshInFlight = useRef(false);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    sortOptionRef.current = sortOption;
    replyRangeDaysRef.current = replyRangeDays;
  }, [sortOption, replyRangeDays]);

  // Track the locked display position of the active conversation
  const lockedActivePositionRef = useRef<{ conversationId: string; index: number } | null>(null);
  
  // Infinite scroll batch size - fixed at 25 items per load
  const BATCH_SIZE = 25;

  // Save and restore conversation scroll state to localStorage
  const getStorageKey = useCallback(() => {
    if (sortOption === 'last-user') {
      return `conversations_state_${sortOption}_${replyRangeDays}`;
    }
    return `conversations_state_${sortOption}`;
  }, [sortOption, replyRangeDays]);

  // Track which conversation is currently in view and save to localStorage (debounced)
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    let scrollSaveTimeout: NodeJS.Timeout;
    const handleScroll = () => {
      clearTimeout(scrollSaveTimeout);
      scrollSaveTimeout = setTimeout(() => {
        // Find the first visible conversation in viewport
        const conversationElements = container.querySelectorAll('[data-conversation-id]');
        let topVisibleConversationId: number | null = null;
        
        for (const el of conversationElements) {
          const rect = el.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          
          // Check if conversation is visible at the top portion of viewport
          if (rect.top >= containerRect.top && rect.top < containerRect.top + 300) {
            const conversationId = parseInt(el.getAttribute('data-conversation-id') || '0', 10);
            if (conversationId > 0) {
              topVisibleConversationId = conversationId;
              break;
            }
          }
        }
        
        if (topVisibleConversationId) {
          lastViewedConversationIdRef.current = topVisibleConversationId;
        }
        
        // Save conversation ID and page to localStorage
        const key = getStorageKey();
        const state = {
          lastConversationId: lastViewedConversationIdRef.current,
          page: currentPageRef.current,
          timestamp: Date.now()
        };
        localStorage.setItem(key, JSON.stringify(state));
      }, 500); // Debounce 500ms
    };
    
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollSaveTimeout);
    };
  }, [getStorageKey]);

  // Restore page and scroll position on mount (runs BEFORE main useEffect)
  // Restore to last viewed conversation on mount
  useEffect(() => {
    const key = getStorageKey();
    const saved = localStorage.getItem(key);
    if (!saved) return;
    
    try {
      const { lastConversationId, page: savedPage, timestamp } = JSON.parse(saved);
      
      // Only restore if saved within last 10 minutes
      const isRecent = Date.now() - timestamp < 10 * 60 * 1000;
      if (!isRecent) {
        console.log('Saved state too old, ignoring');
        localStorage.removeItem(key);
        return;
      }
      
      // Restore if we have a saved conversation ID or page > 1
      if (lastConversationId || (savedPage && savedPage > 1)) {
        // Set flag IMMEDIATELY to block main useEffect
        isRestoringRef.current = true;
        console.log(`🔄 Restoring to conversation ${lastConversationId || 'top of page ' + savedPage}`);
        
        // Load pages until we find the target conversation
        const restoreToConversation = async () => {
          // Don't wait - start loading immediately
          setIsLoading(true);
          let foundConversation = false;
          loadedConversationIds.current.clear();
          
          const pagesToLoad = savedPage || 1;
          
          // Load pages 1 through savedPage (+ 1 for safety if looking for conversation)
          for (let p = 1; p <= (lastConversationId ? pagesToLoad + 1 : pagesToLoad); p++) {
            try {
              const response = await AnalyticsApiService.getConversations({ page: p, limit: BATCH_SIZE });
              const transformed = response.items.map(transformBackendConversation);
              
              // For page 1, replace; for pages 2+, append with deduplication
              if (p === 1) {
                setConversations(transformed);
                // Track loaded IDs
                transformed.forEach((c: any) => {
                  const id = typeof c.id === 'number' ? c.id : parseInt(c.id, 10);
                  loadedConversationIds.current.add(id);
                });
              } else {
                // Deduplicate: only add conversations that aren't already in state
                setConversations(prev => {
                  const existingIds = new Set(prev.map(c => typeof c.id === 'number' ? c.id : parseInt(c.id, 10)));
                  const newConversations = transformed.filter((c: any) => {
                    const id = typeof c.id === 'number' ? c.id : parseInt(c.id, 10);
                    if (existingIds.has(id)) {
                      console.log(`🔍 Skipping duplicate during restoration: ${id} (page ${p})`);
                      return false;
                    }
                    return true;
                  });
                  
                  // Track loaded IDs
                  newConversations.forEach((c: any) => {
                    const id = typeof c.id === 'number' ? c.id : parseInt(c.id, 10);
                    loadedConversationIds.current.add(id);
                  });
                  
                  return [...prev, ...newConversations];
                });
              }
              
              // Check if target conversation is in this batch (if we're looking for one)
              if (lastConversationId) {
                const found = transformed.find((c: any) => {
                  const id = typeof c.id === 'number' ? c.id : parseInt(c.id, 10);
                  return id === lastConversationId;
                });
                
                console.log(`✅ Restoration: loaded page ${p}/${pagesToLoad}${found ? ' - TARGET FOUND!' : ''}`);
                
                if (found) {
                  foundConversation = true;
                  currentPageRef.current = p + 1;
                  break;
                }
                
                // Set currentPageRef for next load as we go
                currentPageRef.current = p + 1;
              } else {
                console.log(`✅ Restoration: loaded page ${p}/${pagesToLoad}`);
                currentPageRef.current = p + 1;
              }
            } catch (error) {
              console.error(`❌ Failed to restore page ${p}:`, error);
              currentPageRef.current = p;
              break;
            }
          }
          
          setIsLoading(false);
          
          // Smooth scroll to the target conversation (if we have one)
          setTimeout(() => {
            if (scrollContainerRef.current && lastConversationId) {
              const targetElement = scrollContainerRef.current.querySelector(
                `[data-conversation-id="${lastConversationId}"]`
              );
              
              if (targetElement) {
                targetElement.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start',
                  inline: 'nearest'
                });
                console.log(`📍 Smooth scrolled to conversation ${lastConversationId}`);
              } else {
                console.warn(`⚠️ Conversation ${lastConversationId} not found in DOM (may have been deleted)`);
              }
            }
          }, 500);
          
          isRestoringRef.current = false;
        };
        
        // Await the restoration to prevent race conditions
        restoreToConversation().catch(err => {
          console.error('Restoration failed:', err);
          isRestoringRef.current = false;
        });
      }
    } catch (error) {
      console.error('Failed to parse saved scroll state:', error);
      isRestoringRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  // Helper function to determine if a conversation is truly a group chat
  const isGroupChat = useCallback((conversation: any): boolean => {
    // Use backend flag as source of truth, validate with participant count
    const participants = conversation.participants || [];
    const participantCount = participants.length;
    
    // Backend determines group chat status, but validate it makes sense
    // Group chat should have 3+ participants OR be flagged as group with 2+ participants
    if (conversation.isGroup) {
      // Trust backend flag if participant count makes sense (2+)
      return participantCount >= 2;
    }
    
    // If backend doesn't flag as group, it's definitely not a group chat
    return false;
  }, []);

  // Transform backend conversation data to frontend format (shared helper)
  const transformBackendConversation = useCallback((backendConv: any): any => {
    return transformConvShared(backendConv);
  }, []);

  const enrichConversationWithFullHistory = useCallback((conversation: any): any => {
    if (!conversation || !Array.isArray(conversation.agent_runs)) {
      return conversation;
    }

    const existingMessages = Array.isArray(conversation.messages) ? conversation.messages : [];
    if (existingMessages.length > 0) {
      return conversation;
    }

    const fullHistory = conversation.agent_runs.flatMap((run: any) =>
      Array.isArray(run.full_message_history) ? run.full_message_history : []
    );
    if (fullHistory.length === 0) {
      return conversation;
    }

    const fallbackCreatedAt = conversation.updated_at || conversation.created_at || new Date().toISOString();
    const normalizedMessages = fullHistory.map((entry: any, idx: number) => {
      const rawContent = entry?.content ?? entry?.text ?? entry?.message ?? entry?.value ?? '';
      const content = typeof rawContent === 'string' ? rawContent : JSON.stringify(rawContent);
      const rawCreatedAt = entry?.created_at ?? entry?.timestamp ?? entry?.ts;
      let createdAt: string;
      if (rawCreatedAt instanceof Date) {
        createdAt = rawCreatedAt.toISOString();
      } else if (typeof rawCreatedAt === 'number') {
        const ms = rawCreatedAt > 1e12 ? rawCreatedAt : rawCreatedAt * 1000;
        createdAt = new Date(ms).toISOString();
      } else if (typeof rawCreatedAt === 'string' && rawCreatedAt) {
        createdAt = rawCreatedAt;
      } else {
        createdAt = fallbackCreatedAt;
      }
      const senderType = entry?.sender_type ?? entry?.sender ?? entry?.role ?? entry?.kind ?? 'user';
      const messageType = entry?.message_type ?? entry?.type ?? 'message';
      const senderId = entry?.sender_id ?? entry?.user_id ?? entry?.author_id;
      const stableId = entry?.id ?? entry?.message_id ?? `full:${conversation.id}:${idx}`;

      return {
        id: stableId,
        conversation_id: conversation.id,
        sender_type: senderType,
        message_type: messageType,
        content,
        created_at: createdAt,
        sender_id: senderId,
        metadata_json: entry?.metadata_json ?? {},
      };
    });

    return {
      ...conversation,
      messages: normalizedMessages,
    };
  }, []);

  /**
   * Intelligent group chat loader - keeps fetching pages until we have target number of group chats
   * Solves the problem where fetching 25 conversations only yields 0-3 group chats
   */
  const loadGroupConversationsUntilTarget = useCallback(async (
    targetCount: number,
    shouldAppend: boolean
  ): Promise<{ conversations: any[]; hasMore: boolean }> => {
    const accumulatedGroupChats: any[] = [];
    let currentPage = shouldAppend ? currentPageRef.current : 1;
    let keepFetching = true;
    const maxPagesToFetch = 10; // Safety limit per scroll event
    let pagesFetched = 0;

    console.log(`🔍 Loading group chats - target: ${targetCount}, starting page: ${currentPage}`);

    while (keepFetching && pagesFetched < maxPagesToFetch && accumulatedGroupChats.length < targetCount) {
      try {
        const pageParams: PageParams = { page: currentPage, limit: BATCH_SIZE };
        const response = await AnalyticsApiService.getGroupConversations(pageParams);
        
        pagesFetched++;
        console.log(`📄 Fetched page ${currentPage} for groups (${response.items.length} items)`);

        if (!response.items || response.items.length === 0) {
          console.log(`🛑 No more conversations from backend`);
          keepFetching = false;
          break;
        }

        // Transform and filter for actual group chats
        const transformed = response.items.map(transformBackendConversation);
        const groups = transformed.filter((conv: any) => conv.isGroup === true);
        
        console.log(`✅ Page ${currentPage}: Found ${groups.length} group chats out of ${transformed.length} total`);

        // Add to accumulated results (with deduplication)
        groups.forEach((group: any) => {
          const groupId = typeof group.id === 'number' ? group.id : parseInt(group.id, 10);
          if (!loadedConversationIds.current.has(groupId)) {
            accumulatedGroupChats.push(group);
            loadedConversationIds.current.add(groupId);
          }
        });

        console.log(`📊 Accumulated ${accumulatedGroupChats.length}/${targetCount} group chats so far`);

        // Check if backend has more pages
        if (response.items.length < BATCH_SIZE) {
          console.log(`🛑 Backend returned partial page - no more data`);
          keepFetching = false;
        }

        currentPage++;
      } catch (error) {
        console.error(`❌ Failed to fetch page ${currentPage}:`, error);
        keepFetching = false;
      }
    }

    // Determine if there are more groups to load
    // Note: accumulatedGroupChats.length = groups found in THIS function call only
    let hasMoreGroups = false;
    const groupsFoundInThisCall = accumulatedGroupChats.length;
    
    if (pagesFetched >= maxPagesToFetch) {
      // Hit safety limit (fetched 10 pages) - check if we made progress
      if (groupsFoundInThisCall === 0) {
        // Found NO groups in 10 pages - definitely no more exist
        console.warn(`⚠️ Hit max pages (${maxPagesToFetch}) with 0 groups found - END OF DATA`);
        hasMoreGroups = false;
      } else if (groupsFoundInThisCall < 5) {
        // Found very few groups (< 5 in 10 pages) - probably near the end, but allow one more try
        console.warn(`⚠️ Hit max pages (${maxPagesToFetch}) with only ${groupsFoundInThisCall} groups - might be near end`);
        hasMoreGroups = true; // Allow one more scroll attempt
      } else {
        // Found decent number of groups (>= 5 in 10 pages) - more likely exist
        console.log(`✅ Hit max pages but found ${groupsFoundInThisCall} groups - likely more available`);
        hasMoreGroups = true;
      }
    } else if (!keepFetching && groupsFoundInThisCall < targetCount) {
      // Backend ran out before reaching target (returned empty page)
      hasMoreGroups = false;
      console.log(`🛑 Backend exhausted: ${groupsFoundInThisCall} groups found (less than target ${targetCount})`);
    } else if (accumulatedGroupChats.length >= targetCount) {
      // Successfully reached target - likely more groups exist
      hasMoreGroups = true;
      console.log(`✅ Target reached: ${accumulatedGroupChats.length} groups - more likely available`);
    } else {
      // Shouldn't reach here, but default to false for safety
      hasMoreGroups = false;
      console.log(`🛑 Unknown state: ${accumulatedGroupChats.length} groups loaded`);
    }

    // Update current page ref for next load
    currentPageRef.current = currentPage;

    console.log(`✅ Group chat load complete: ${accumulatedGroupChats.length} groups collected, hasMore: ${hasMoreGroups}`);

    return {
      conversations: accumulatedGroupChats,
      hasMore: hasMoreGroups
    };
  }, [BATCH_SIZE, transformBackendConversation]);

  // Load conversations from backend with page-based infinite scroll
  // Uses series-agent-service /admin/conversations endpoint with optional search parameter
  const loadConversations = useCallback(async (shouldAppend: boolean = false, loadVersion?: number) => {
    try {
      if (listRefreshInFlight.current) return;
      listRefreshInFlight.current = true;
      const activeLoadVersion = loadVersion ?? loadGenerationRef.current;
      
      // Set appropriate loading state
      if (shouldAppend) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
        setError(null);
      }
      
      const currentPage = shouldAppend ? currentPageRef.current : 1;
      const activeSort = sortOptionRef.current;
      const activeReplyRangeDays = replyRangeDaysRef.current;
      const isAuthorSort = activeSort === 'author-posts' || activeSort === 'author-replies';
      const isAuthorCountSort = activeSort === 'author-replies-count';
      const isEventUsersSort = activeSort === 'event-users';
      const isAuthorIdsSort = isAuthorSort || isAuthorCountSort;
      let paginatedResponse: PaginatedConversations | null = null;
      let rawItems: any[] = [];
      let rawStats: Record<string, any> = {};
      let responseHasMore = false;

      if (isAuthorSort) {
        if (!shouldAppend) {
          authorCursorRef.current = null;
        }
        const cursor = shouldAppend ? authorCursorRef.current : null;
        const authorResponse = activeSort === 'author-posts'
          ? await AnalyticsApiService.getPostAuthorIds({ cursor: cursor ?? undefined, limit: BATCH_SIZE })
          : await AnalyticsApiService.getReplyAuthorIds({ cursor: cursor ?? undefined, limit: BATCH_SIZE });
        const authorItems = authorResponse.items || [];
        const authorIds = authorItems.map((item) => item.authorId).filter(Boolean);
        authorCursorRef.current = authorResponse.nextCursor ?? null;
        responseHasMore = Boolean(authorResponse.hasMore);

        if (authorIds.length > 0) {
          const batches: string[][] = [];
          for (let i = 0; i < authorIds.length; i += 100) {
            batches.push(authorIds.slice(i, i + 100));
          }
          const responses = await Promise.all(
            batches.map((batch) =>
              AnalyticsApiService.getConversationsByUserIds({
                user_ids: batch,
              })
            )
          );
          rawItems = responses.flatMap((response) => response.items || []);
        }

        console.log(`Loaded ${rawItems.length} conversations from author ids (page: ${currentPage})`);
      } else if (isAuthorCountSort) {
        if (!shouldAppend) {
          replyCountCursorRef.current = null;
        }
        const cursor = shouldAppend ? replyCountCursorRef.current : null;
        const authorResponse = await AnalyticsApiService.getReplyAuthorCounts({
          cursor: cursor ?? undefined,
          limit: BATCH_SIZE,
        });
        const authorItems = authorResponse.items || [];
        const authorIds = authorItems.map((item) => item.authorId).filter(Boolean);
        replyCountCursorRef.current = authorResponse.nextCursor ?? null;
        responseHasMore = Boolean(authorResponse.hasMore);

        if (authorIds.length > 0) {
          const batches: string[][] = [];
          for (let i = 0; i < authorIds.length; i += 100) {
            batches.push(authorIds.slice(i, i + 100));
          }
          const responses = await Promise.all(
            batches.map((batch) =>
              AnalyticsApiService.getConversationsByUserIds({
                user_ids: batch,
              })
            )
          );
          rawItems = responses.flatMap((response) => response.items || []);
        }

        console.log(`Loaded ${rawItems.length} conversations from reply counts (page: ${currentPage})`);
      } else if (isEventUsersSort) {
        if (!shouldAppend) {
          eventUserOffsetRef.current = 0;
          eventUserIdsRef.current = [];
        }

        if (eventUserIdsRef.current.length === 0) {
          const eventUserIds = await AnalyticsApiService.getEventRsvpUserIds();
          const cleanedIds = eventUserIds
            .map((id) => String(id || '').trim())
            .filter(Boolean);
          eventUserIdsRef.current = Array.from(new Set(cleanedIds));
        }

        const offset = shouldAppend ? eventUserOffsetRef.current : 0;
        const pageUserIds = eventUserIdsRef.current.slice(offset, offset + BATCH_SIZE);
        eventUserOffsetRef.current = offset + pageUserIds.length;
        responseHasMore = eventUserOffsetRef.current < eventUserIdsRef.current.length;

        if (pageUserIds.length > 0) {
          const batches: string[][] = [];
          for (let i = 0; i < pageUserIds.length; i += 100) {
            batches.push(pageUserIds.slice(i, i + 100));
          }
          const responses = await Promise.all(
            batches.map((batch) =>
              AnalyticsApiService.getConversationsByUserIds({
                user_ids: batch,
              })
            )
          );
          rawItems = responses.flatMap((response) => response.items || []);
        }

        console.log(`Loaded ${rawItems.length} conversations from event user ids (page: ${currentPage})`);
      } else if (activeSort === 'last-user') {
        console.log(`Loading user replies (page ${currentPage}, lookback ${activeReplyRangeDays}d)`);
        paginatedResponse = await AnalyticsApiService.getUserReplyConversations({
          page: currentPage,
          limit: BATCH_SIZE,
          lookback_days: activeReplyRangeDays,
        });
      } else {
        const pageParams: PageParams = {
          page: currentPage,
          limit: BATCH_SIZE,
          sort_by: 'last_activity',
        };
        console.log(`Loading conversations (page ${currentPage})`);
        paginatedResponse = await AnalyticsApiService.getConversations(pageParams);
      }

      if (paginatedResponse) {
        rawItems = paginatedResponse.items || [];
        rawStats = paginatedResponse.stats || {};
        responseHasMore = rawItems.length >= BATCH_SIZE;
        console.log(`Loaded ${rawItems.length} conversations from backend (page: ${currentPage})`);
      }
      
      if (activeLoadVersion !== loadGenerationRef.current) {
        console.log('Skipping stale load results after filter change');
        return;
      }

      // Handle empty results
      if (!rawItems || rawItems.length === 0) {
        if (!shouldAppend) {
          // If this is initial load and no results, clear conversations
          setConversations([]);
          loadedConversationIds.current.clear();
        }
        setHasMore(false);
        return;
      }

      // Update total count if available
      if (rawStats.total && (totalConversationCount === null || totalConversationCount === 0)) {
        setTotalConversationCount(rawStats.total);
      }

      // Transform backend data to frontend format
      const transformedConversations = rawItems.map(transformBackendConversation);

      const userReplyOnly = activeSort === 'last-user'
        ? transformedConversations.filter((conv: any) => {
            const ts = conv.lastUserMessageTime;
            return ts instanceof Date && !Number.isNaN(ts.getTime?.());
          })
        : transformedConversations;

      // For user-reply sorting, keep the current page ordered before appending
      const preparedConversations = activeSort === 'last-user'
        ? [...userReplyOnly].sort((a: any, b: any) => {
            const aTime = a.lastUserMessageTime?.getTime?.() || 0;
            const bTime = b.lastUserMessageTime?.getTime?.() || 0;
            return bTime - aTime;
          })
        : userReplyOnly;
      
      console.log(`Prepared ${preparedConversations.length} items for filter: ${activeSort}`);
      
      // For initial load, clear the Set and start fresh
      if (!shouldAppend) {
        loadedConversationIds.current.clear();
      }

      // Filter out duplicates based on conversation ID
      const newConversations = preparedConversations.filter((conv: any) => {
        const convId = typeof conv.id === 'number' ? conv.id : parseInt(conv.id, 10);
        if (loadedConversationIds.current.has(convId)) {
          console.log(`Skipping duplicate conversation ID: ${convId}`);
          return false; // Skip duplicate
        }
        loadedConversationIds.current.add(convId);
        return true;
      });

      console.log(`After duplicate filter: ${newConversations.length} new conversations (Set size: ${loadedConversationIds.current.size})`);

      // Append or replace conversations directly (read state N+1 query removed for performance)
      if (shouldAppend) {
        setConversations(prev => [...prev, ...newConversations as any]);
      } else {
        setConversations(newConversations as any);
      }

      // Update page for next load (always increment after successful load)
      currentPageRef.current = currentPage + 1;

      // Update hasMore based on whether we got a full batch
      const hasMoreFlag = (isAuthorIdsSort || isEventUsersSort) ? responseHasMore : rawItems.length >= BATCH_SIZE;
      setHasMore(hasMoreFlag);
      console.log(`Infinite scroll: page ${currentPage} loaded, next page: ${currentPageRef.current}, hasMore: ${hasMoreFlag}`);
    } catch (error) {
      console.error('Failed to load conversations:', error);
      setError('Failed to load conversations from backend.');
      if (!shouldAppend) {
        setConversations([]);
        loadedConversationIds.current.clear();
      }
      setHasMore(false);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
      listRefreshInFlight.current = false;

      if (pendingFilterReloadRef.current) {
        pendingFilterReloadRef.current = false;
        currentPageRef.current = 1;
        loadedConversationIds.current.clear();
        setConversations([]);
        setIsLoading(true);
        setHasMore(true);
        setTimeout(() => {
          loadConversations(false);
        }, 0);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [BATCH_SIZE, transformBackendConversation, sortOption, replyRangeDays]);

  // Fetch total conversation count (separate from paginated list)
  const fetchTotalCount = useCallback(async () => {
    try {
      const countData = await AnalyticsApiService.getTotalConversationCount();
      setTotalConversationCount(countData.total_conversations);
      console.log(`Total conversation count updated: ${countData.total_conversations}`);
    } catch (error) {
      console.error('Failed to fetch total conversation count:', error);
      // Silently fail and use pagination total as fallback
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load conversations when debounced search or sort option changes
  useEffect(() => {
    loadGenerationRef.current += 1;
    // Check if there's a saved state that will trigger restoration
    const key = getStorageKey();
    const saved = localStorage.getItem(key);
    if (saved) {
      try {
        const { lastConversationId, page: savedPage, timestamp } = JSON.parse(saved);
        const isRecent = Date.now() - timestamp < 10 * 60 * 1000;
        
        // If we have recent saved state, let the restoration useEffect handle the initial load
        if (isRecent && (lastConversationId || (savedPage && savedPage > 1))) {
          console.log('⏭️ Skipping initial load - restoration will handle it');
          return;
        }
      } catch (e) {
        // Invalid saved state, proceed with normal load
      }
    }
    
    // Skip if we're in the middle of restoration
    if (isRestoringRef.current) {
      console.log('⏭️ Skipping load - restoration in progress');
      return;
    }
    
    if (sortOption === 'last-user') {
      console.log(`Filter changed to: ${sortOption} (${replyRangeDays}d), loading data...`);
    } else {
      console.log(`Filter changed to: ${sortOption}, loading data...`);
    }
    // Clear existing data to prevent type mismatches when switching filters
    setConversations([]);
    setIsLoading(true);
    setHasMore(true);
    // Reset page to 1 and load fresh data
    currentPageRef.current = 1;
    loadedConversationIds.current.clear();
    authorCursorRef.current = null;
    eventUserIdsRef.current = [];
    eventUserOffsetRef.current = 0;

    // If a load is already in flight, queue a refresh for when it finishes
    if (listRefreshInFlight.current) {
      console.log('⏳ Filter change queued - load in flight');
      pendingFilterReloadRef.current = true;
      return;
    }
    
    // Use setTimeout to ensure state updates are batched
    const timer = setTimeout(() => {
      loadConversations(false);
      fetchTotalCount();
    }, 0);
    
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortOption, replyRangeDays]);

  // Smart auto-refresh with page visibility detection and lazy loading
  useEffect(() => {
    // Respect global config to disable list auto-refresh entirely
    if (!isFeatureEnabled('ENABLE_AUTO_REFRESH')) {
      return;
    }
    let interval: ReturnType<typeof setInterval> | null = null;
    let isPageVisible = true;
    
    const getRefreshInterval = () => {
      if (!isPageVisible) {
        return 10 * 60 * 1000; // 10 minutes when tab is hidden
      }
      // When a chat is active or user interacting, pause list refresh
      if (activeConversationId || isTyping || isInspecting) return 60 * 60 * 1000;
      return 2 * 60 * 1000; // 2 minutes for normal browsing
    };

    const scheduleRefresh = () => {
      if (interval) {
        clearInterval(interval);
      }
      
      interval = setInterval(() => {
        if (!isLoading && isPageVisible && !activeConversationId && !isTyping && !isInspecting) {
          if (import.meta.env.VITE_ENABLE_ANALYTICS === 'true') {
            console.debug('listRefresh:', { visible: isPageVisible, active: !!activeConversationId, interacting: (isTyping || isInspecting) });
          }
          currentPageRef.current = 1;
          loadConversations(false);
        }
      }, getRefreshInterval());
    };

    const handleVisibilityChange = () => {
      isPageVisible = document.visibilityState === 'visible';
      console.log(`Page visibility changed: ${isPageVisible ? 'visible' : 'hidden'}`);
      
      if (isPageVisible && !activeConversationId && !isTyping && !isInspecting) {
        // Immediately refresh when page becomes visible
        if (!isLoading) {
          currentPageRef.current = 1;
          loadConversations(false);
        }
      }
      
      // Reschedule with new interval
      scheduleRefresh();
    };

    // Set up visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Initial schedule
    scheduleRefresh();

    return () => {
      if (interval) {
        clearInterval(interval);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadConversations, isLoading, activeConversationId, isTyping, isInspecting, sortOption]);

  // Search is now handled by separate SearchPage component - no search logic needed here

  // Apply frontend filtering as a safety check (backend should already filter)
  const filteredConversations = useMemo(() => {
    if (sortOption === 'last-user') {
      return conversations.filter((conv: any) => {
        const ts = conv.lastUserMessageTime;
        return ts instanceof Date && !Number.isNaN(ts.getTime?.());
      });
    }
    return conversations;
  }, [conversations, sortOption]);

  // Sort conversations (filtering is handled separately above)
  // Keep active conversation in place to prevent it jumping around when messages are sent
  const sortedConversations = useMemo(() => {
    const sorted = [...filteredConversations];
    
    // Sort based on selected option
    let sortedList;
    switch (sortOption) {
      case 'new-old':
        sortedList = sorted.sort((a, b) => {
          const aTime = a.lastMessageTime?.getTime?.() || 0;
          const bTime = b.lastMessageTime?.getTime?.() || 0;
          return bTime - aTime;
        });
        break;
      case 'author-posts':
      case 'author-replies':
      case 'author-replies-count':
      case 'event-users':
      case 'last-user':
        // Keep per-page ordering for user-reply sort to avoid reshuffling on append
        sortedList = sorted;
        break;
      default:
        sortedList = sorted.sort((a, b) => {
          const aTime = a.lastMessageTime?.getTime?.() || 0;
          const bTime = b.lastMessageTime?.getTime?.() || 0;
          return bTime - aTime;
        });
    }
    
    // If there's an active conversation with a locked position, maintain that position
    if (activeConversationId && lockedActivePositionRef.current?.conversationId === String(activeConversationId)) {
      const activeConversation = sortedList.find(c => parseInt(c.id, 10) === activeConversationId);
      const lockedIndex = lockedActivePositionRef.current.index;
      
      if (activeConversation) {
        // Remove from its current sorted position
        const currentIndex = sortedList.findIndex(c => parseInt(c.id, 10) === activeConversationId);
        if (currentIndex !== -1) {
          sortedList.splice(currentIndex, 1);
        }
        
        // Insert at the locked position (ensure it's within bounds)
        const insertIndex = Math.min(lockedIndex, sortedList.length);
        sortedList.splice(insertIndex, 0, activeConversation);
      }
    }
    
    return sortedList;
  }, [filteredConversations, sortOption, activeConversationId]);

  // Message sequence tracking to prevent race conditions
  const messageSequenceRef = useRef(0);
  const pendingMessagesRef = useRef(new Set<string>());
  // Track messages from SSE with TTL to avoid unbounded growth
  const liveUpdateMessageIds = useRef(new Map<string, number>());
  // Track conversations that have been fully hydrated once (no since_ts)
  const hydratedConversationIds = useRef(new Set<string>());
  // Prevent overlapping refreshes
  const activeRefreshInFlight = useRef(false);

  const handleSendMessage = async (conversationId: string, text: string) => {
    const timestamp = new Date();
    const messageSequence = ++messageSequenceRef.current;
    const messageId = `${conversationId}-${messageSequence}-${Date.now()}`;
    
    const newMessage: Message = {
      id: messageId,
      text,
      timestamp: timestamp.toLocaleString(undefined, { 
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric', 
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
        // use browser local time
      }),
      isOwn: true,  // Set to true so AI messages appear as blue bubbles
      createdAt: timestamp,
      deliveryStatus: 'sending',
    };

    // Track pending message to prevent race conditions
    pendingMessagesRef.current.add(messageId);

    // Optimistically update the UI
    setConversations(prev => prev.map(conv => {
      if (conv.id === conversationId) {
        return {
          ...conv,
          messages: [...conv.messages, newMessage],
          lastMessageTime: timestamp
        };
      }
      return conv;
    }));

    try {
      const conversation = conversations.find(c => c.id === conversationId);
      const recipientUserId = getPrimaryConversationUserId(conversation);
      if (!recipientUserId) {
        throw new Error(`Unable to determine recipient user_id for conversation ${conversationId}`);
      }
      
      // Convert string conversation ID to number for the API
      const convIdNumber = parseInt(conversationId, 10);
      
      await AnalyticsApiService.sendAsAI(convIdNumber, text, recipientUserId);
      
      // Invalidate cache since conversation has new message
      conversationCache.invalidate(convIdNumber);
      
      // Mark message as successfully sent (keep it visible)
      setConversations(prev => prev.map(conv => {
        if (conv.id !== conversationId) return conv;
        return {
          ...conv,
          messages: conv.messages.map(m => m.id === messageId ? { ...m, deliveryStatus: 'sent', deliveryError: undefined } : m),
        };
      }));

      // Mark message as successfully sent
      pendingMessagesRef.current.delete(messageId);
    } catch (error) {
      console.error('Failed to send message:', error);
      
      // Remove from pending set
      pendingMessagesRef.current.delete(messageId);
      
      // Keep the message visible, but mark as failed ("Not delivered")
      const errorMessage = error instanceof Error ? error.message : String(error);
      setConversations(prev => prev.map(conv => {
        if (conv.id !== conversationId) return conv;
        return {
          ...conv,
          messages: conv.messages.map(m => m.id === messageId
            ? { ...m, deliveryStatus: 'failed', deliveryError: errorMessage }
            : m
          ),
        };
      }));
    }
  };

  // Merge helper: de-duplicate by id and sort by createdAt ASC
  const mergeMessages = useCallback((oldMsgs: Message[], incomingMsgs: Message[]): Message[] => {
    // Robust dedupe across API and SSE with id/messageHandle/runId and ms precision
    const byPrimary = new Map<string, Message>();
    const aliasToPrimary = new Map<string, string>();

    const normalizeText = (t: string | undefined) => (t || '').trim().replace(/\s+/g, ' ');
    const msIso = (d?: Date) => (d && !Number.isNaN(d.getTime()) ? d.toISOString() : '');

    const idKey = (m: Message) => (m.id ? `id:${m.id}` : undefined);
    const handleKey = (m: Message) => (m.messageHandle ? `h:${m.messageHandle}` : undefined);
    const runKey = (m: Message) => (m.runId ? `r:${m.runId}` : undefined);
    const strongCompositeKey = (m: Message) =>
      `sc:${m.senderId || m.sender || ''}|${msIso(m.createdAt)}|${normalizeText(m.text)}`;

    const primaryKeyFor = (m: Message): string => idKey(m) || handleKey(m) || runKey(m) || strongCompositeKey(m);
    const aliasKeysFor = (m: Message): string[] => {
      const keys: string[] = [];
      const ik = idKey(m);
      if (ik) keys.push(ik);
      const hk = handleKey(m);
      if (hk) keys.push(hk);
      const rk = runKey(m);
      if (rk) keys.push(rk);
      keys.push(strongCompositeKey(m));
      return keys;
    };

    // Seed with existing messages
    for (const m of oldMsgs) {
      const pKey = primaryKeyFor(m);
      byPrimary.set(pKey, m);
      for (const alias of aliasKeysFor(m)) aliasToPrimary.set(alias, pKey);
    }

    const upsert = (m: Message) => {
      const pKey = primaryKeyFor(m);
      // Direct hit on primary id/handle/run/composite
      let existingKey = byPrimary.has(pKey) ? pKey : undefined;
      if (!existingKey) {
        // Try alias matches in order of strength
        for (const alias of aliasKeysFor(m)) {
          const mapped = aliasToPrimary.get(alias);
          if (mapped && byPrimary.has(mapped)) {
            existingKey = mapped;
            break;
          }
        }
      }
      if (!existingKey) {
        // New message
        byPrimary.set(pKey, m);
        for (const alias of aliasKeysFor(m)) aliasToPrimary.set(alias, pKey);
        return;
      }
      const existing = byPrimary.get(existingKey)!;
      // Prefer incoming if it has a stronger identifier (id/handle/run) or newer payload
      const incomingHasId = Boolean(m.id);
      const existingHasId = Boolean(existing.id);
      const incomingHasHandle = Boolean(m.messageHandle);
      const existingHasHandle = Boolean(existing.messageHandle);
      const incomingHasRun = Boolean(m.runId);
      const existingHasRun = Boolean(existing.runId);
      const newer = (m.createdAt?.getTime?.() || 0) > (existing.createdAt?.getTime?.() || 0);

      const stronger =
        (incomingHasId && !existingHasId) ||
        (incomingHasHandle && !existingHasHandle) ||
        (incomingHasRun && !existingHasRun);

      const merged = stronger || newer ? { ...existing, ...m } : existing;
      // If key becomes stronger (e.g., gains id/handle), move to new primary key
      const newPrimary = primaryKeyFor(merged);
      if (newPrimary !== existingKey) {
        byPrimary.delete(existingKey);
        byPrimary.set(newPrimary, merged);
        for (const alias of aliasKeysFor(merged)) aliasToPrimary.set(alias, newPrimary);
      } else {
        byPrimary.set(existingKey, merged);
      }
    };

    for (const m of incomingMsgs) upsert(m);

    const merged = Array.from(byPrimary.values());
    merged.sort((a, b) => (a.createdAt?.getTime?.() || 0) - (b.createdAt?.getTime?.() || 0));

    // Fold reaction event messages into their referenced message and remove standalone reaction messages.
    const folded = foldReactionMessages(merged);

    // Safety: never lose non-reaction messages due to merge/dedupe.
    const oldNonReactionCount = oldMsgs.filter((m) => String(m.messageType || '').toLowerCase() !== 'reaction').length;
    if (folded.length < oldNonReactionCount) {
      if (import.meta.env.VITE_ENABLE_ANALYTICS === 'true') {
        console.debug('[merge] prevented shrink: merged', folded.length, 'oldNonReaction', oldNonReactionCount);
      }
      return foldReactionMessages(oldMsgs);
    }
    return folded;
  }, []);

  const handleReactMessage = useCallback(async (conversationId: string, messageId: string, reaction: ReactionKind) => {
    const convIdNumber = parseInt(conversationId, 10);
    const conversation = conversations.find(c => c.id === conversationId);
    const recipientUserId = getPrimaryConversationUserId(conversation);
    if (!recipientUserId) {
      throw new Error(`Unable to determine recipient user_id for conversation ${conversationId}`);
    }

    await AnalyticsApiService.sendManualReaction(convIdNumber, recipientUserId, messageId, reaction);
    conversationCache.invalidate(convIdNumber);

    // Optimistically attach the reaction to the target message (iMessage-style overlay),
    // so we don't render a standalone "love" message bubble.
    setConversations(prev => prev.map(conv => {
      if (conv.id !== conversationId) return conv;
      return {
        ...conv,
        messages: conv.messages.map(msg => {
          if (msg.id !== messageId) return msg;
          const currentMetadata = msg.metadata ?? {};
          const existingRaw = Array.isArray(currentMetadata.reactions)
            ? currentMetadata.reactions
            : Array.isArray(currentMetadata.tapbacks)
              ? currentMetadata.tapbacks
              : Array.isArray(currentMetadata.reaction_summary)
                ? currentMetadata.reaction_summary
                : [];
          const nextReactionKey = String(reaction).toLowerCase();
          const alreadyHas = existingRaw.some((item: any) => {
            if (typeof item === 'string') return item.toLowerCase() === nextReactionKey;
            if (item && typeof item === 'object') {
              const typed = item as Record<string, any>;
              const typeValue =
                typeof typed.reaction === 'string'
                  ? typed.reaction
                  : typeof typed.type === 'string'
                    ? typed.type
                    : undefined;
              return typeof typeValue === 'string' && typeValue.toLowerCase() === nextReactionKey;
            }
            return false;
          });
          const nextReactions = alreadyHas ? existingRaw : [...existingRaw, nextReactionKey];
          return {
            ...msg,
            metadata: { ...currentMetadata, reactions: nextReactions },
          };
        }),
      };
    }));

    // Refresh this conversation to pick up reaction metadata/messages immediately
    try {
      const fresh = await AnalyticsApiService.getConversation(convIdNumber);
      const transformedConv = transformBackendConversation(fresh);
      setConversations(prev => prev.map(conv => {
        if (conv.id !== conversationId) return conv;
        const merged = mergeMessages(conv.messages, transformedConv.messages);
        const last = merged.length ? merged[merged.length - 1].createdAt : conv.lastMessageTime;
        const lastUserMessageTime = transformedConv.lastUserMessageTime ?? conv.lastUserMessageTime;
        return { ...transformedConv, messages: merged, lastMessageTime: last, lastUserMessageTime };
      }));
      hydratedConversationIds.current.add(conversationId);
    } catch (e) {
      console.warn('Reaction sent, but failed to refresh conversation (non-fatal):', e);
    }
  }, [conversations, mergeMessages, transformBackendConversation]);

  // Handle incoming live messages from SSE
  const handleLiveMessage = useCallback((conversationId: string, messageData: any) => {
    if (!conversationId) return;
    if (!messageData || typeof messageData !== 'object') {
      console.warn('📨 Live message missing/invalid payload for conversation:', conversationId, messageData);
      return;
    }

    // Some SSE payloads wrap the message under `message`
    const raw = (messageData as any).message && typeof (messageData as any).message === 'object'
      ? (messageData as any).message
      : messageData;

    const normalizeText = (value: string) => value.trim().replace(/\s+/g, ' ');

    const textRaw =
      typeof raw.content === 'string'
        ? raw.content
        : typeof raw.message === 'string'
          ? raw.message
          : typeof raw.text === 'string'
            ? raw.text
            : '';
    const text = normalizeText(String(textRaw || ''));

    const createdAtRaw = raw.created_at ?? raw.createdAt ?? raw.timestamp ?? null;
    const createdAt =
      typeof createdAtRaw === 'string'
        ? (parseVectorDbUtc(createdAtRaw) ?? new Date(createdAtRaw))
        : (createdAtRaw instanceof Date ? createdAtRaw : new Date());

    // Sender identity is critical for dedupe (mergeMessages uses senderId/sender in composite key)
    const sender =
      raw.sender != null
        ? String(raw.sender)
        : raw.sender_type != null
          ? String(raw.sender_type)
          : raw.senderType != null
            ? String(raw.senderType)
            : undefined;
    const senderId =
      raw.sender_id != null
        ? String(raw.sender_id)
        : raw.senderId != null
          ? String(raw.senderId)
          : undefined;
    const senderName =
      typeof raw.sender_name === 'string'
        ? raw.sender_name
        : typeof raw.senderName === 'string'
          ? raw.senderName
          : undefined;

    const messageHandle =
      typeof raw.message_handle === 'string'
        ? raw.message_handle
        : typeof raw.messageHandle === 'string'
          ? raw.messageHandle
          : undefined;
    const runIdRaw = raw.run_id ?? raw.agent_run_id ?? raw.graph_run_id ?? raw.runId ?? null;
    const runId = runIdRaw != null ? String(runIdRaw) : undefined;

    const serverIdRaw = raw.id ?? raw.message_id ?? null;
    const serverId = serverIdRaw != null ? String(serverIdRaw) : undefined;

    // Deterministic logical key for SSE dedupe (avoids `Date.now()` fallbacks causing duplicates)
    const compositeKey = `sc:${senderId || sender || ''}|${createdAt.toISOString()}|${text}`;
    const dedupeKey =
      serverId
        ? `id:${serverId}`
        : messageHandle
          ? `h:${messageHandle}`
          : runId
            ? `r:${runId}`
            : compositeKey;

    // Skip if we've already processed this logical message from SSE
    if (liveUpdateMessageIds.current.has(dedupeKey)) {
      console.log('⏭️ Skipping duplicate SSE message:', dedupeKey);
      return;
    }
    liveUpdateMessageIds.current.set(dedupeKey, Date.now());

    const senderLower = (sender || '').toLowerCase();
    const senderTypeLower = String(raw.sender_type ?? raw.senderType ?? '').toLowerCase();
    const isAi =
      senderLower === 'ai_friend' ||
      senderLower === 'agent' ||
      senderTypeLower === 'ai_friend' ||
      senderTypeLower === 'agent';
    const isUserMessage =
      senderTypeLower === 'user' ||
      senderTypeLower === 'human' ||
      senderLower === 'user' ||
      senderLower === 'human';

    const messageId =
      serverId ??
      (messageHandle ? `h:${messageHandle}` : (runId ? `r:${runId}` : compositeKey));

    const newMessage: Message = {
      id: messageId,
      text,
      timestamp: typeof raw.created_at === 'string'
        ? formatVectorDBTimestampWithDate(raw.created_at)
        : createdAt.toLocaleString('en-US', {
          weekday: 'short',
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          second: '2-digit',
          hour12: true,
          timeZone: 'America/New_York',
        }),
      isOwn: isAi,
      createdAt,
      messageType: raw.message_type || raw.messageType,
      sender,
      senderId,
      senderName,
      messageHandle,
      runId,
      metadata: raw.metadata_json ?? raw.metadata ?? undefined,
    };

    // Update conversation with new message
    setConversations(prev => prev.map(conv => {
      if (conv.id === conversationId) {
        console.log('✅ Adding live message to conversation:', conversationId);
        const merged = mergeMessages(conv.messages, [newMessage]);
        const last = merged.length ? merged[merged.length - 1].createdAt : conv.lastMessageTime;
        const lastUserMessageTime = isUserMessage
          ? (!conv.lastUserMessageTime || createdAt > conv.lastUserMessageTime ? createdAt : conv.lastUserMessageTime)
          : conv.lastUserMessageTime;
        return { ...conv, messages: merged, lastMessageTime: last, lastUserMessageTime };
      }
      return conv;
    }));
  }, []);

  // Auto-refresh active conversation periodically to catch any missed updates
  useEffect(() => {
    if (!activeConversationId) return;

    let interval: ReturnType<typeof setInterval> | null = null;

    const refreshActiveConversation = async () => {
      if (activeRefreshInFlight.current) {
        return;
      }
      activeRefreshInFlight.current = true;
      try {
        const convIdNumber = typeof activeConversationId === 'number' ? activeConversationId : parseInt(activeConversationId, 10);
        // If not hydrated yet, do nothing here; initial full hydrate happens on activation
        const isHydrated = hydratedConversationIds.current.has(String(activeConversationId));
        const lastTs = (() => {
          const active = conversations.find(c => parseInt(c.id, 10) === activeConversationId);
          const last = active?.messages?.[active.messages.length - 1]?.createdAt;
          if (!last) return '';
          // Nudge by +1ms to avoid equal-timestamp edge
          const nudged = new Date(last.getTime() + 1);
          return nudged.toISOString();
        })();
        const freshData = isHydrated && lastTs
          ? await AnalyticsApiService.getConversationSince(convIdNumber, lastTs)
          : await AnalyticsApiService.getConversation(convIdNumber);
        const transformedConv = transformBackendConversation(freshData);

        // Merge messages instead of replacing to preserve SSE-delivered messages
        setConversations(prev => prev.map(conv => {
          if (conv.id !== activeConversationId) return conv;
          const merged = mergeMessages(conv.messages, transformedConv.messages);
          const last = merged.length ? merged[merged.length - 1].createdAt : conv.lastMessageTime;
          // No-op guard: if nothing changed, preserve existing object
          if (merged.length === conv.messages.length && last.getTime?.() === conv.lastMessageTime.getTime?.()) {
            return conv;
          }
          if (import.meta.env.VITE_ENABLE_ANALYTICS === 'true') {
            console.debug('[refresh] merged', merged.length, 'prev', conv.messages.length, 'cid', activeConversationId);
          }
          const lastUserMessageTime = transformedConv.lastUserMessageTime ?? conv.lastUserMessageTime;
          return { ...transformedConv, messages: merged, lastMessageTime: last, lastUserMessageTime };
        }));

        console.log('🔄 Refreshed active conversation (merged):', activeConversationId);
      } catch (error) {
        console.error('Failed to refresh active conversation:', error);
      } finally {
        activeRefreshInFlight.current = false;
      }
    };

    const schedule = async () => {
      try {
        const { sseManager } = await import('./services/sseConnectionManager');
        const stats = sseManager.getStats();
        const connected = stats.isConnected;
        const ms = connected ? 2 * 60 * 1000 : 30 * 1000; // 2m when SSE ok, else 30s
        if (interval) clearInterval(interval);
        interval = setInterval(refreshActiveConversation, ms);
      } catch {
        // If sseManager unavailable, fallback to 30s
        if (interval) clearInterval(interval);
        interval = setInterval(refreshActiveConversation, 30000);
      }
    };

    schedule();
    return () => { if (interval) clearInterval(interval); };
  }, [activeConversationId, transformBackendConversation]);

  // Prefetch next TWO conversations for instant activation
  useEffect(() => {
    if (!activeConversationId || sortedConversations.length < 2) return;
    
    // Find current conversation index
    const currentIndex = sortedConversations.findIndex(c => 
      String(c.id) === String(activeConversationId)
    );
    
    if (currentIndex === -1) return;
    
    // Get next 2 conversations
    const conversationsToPrefetch = [];
    for (let i = 1; i <= 2; i++) {
      const nextIndex = currentIndex + i;
      if (nextIndex < sortedConversations.length) {
        conversationsToPrefetch.push(sortedConversations[nextIndex]);
      }
    }
    
    if (conversationsToPrefetch.length === 0) return;
    
    console.log(`🔮 Prefetching next ${conversationsToPrefetch.length} conversations: ${conversationsToPrefetch.map(c => c.id).join(', ')}`);
    
    // Prefetch each conversation in background
    conversationsToPrefetch.forEach((nextConversation, index) => {
      const nextId = nextConversation.id;
      
      // Only prefetch if not already hydrated
      if (!hydratedConversationIds.current.has(nextId)) {
        const convIdNumber = parseInt(nextId, 10);
        
        // Check if already in cache
        if (conversationCache.get(convIdNumber)) {
          console.log(`🔮 Conversation ${nextId} already cached (skipping prefetch)`);
          return;
        }
        
        // Add small delay for second prefetch to avoid overwhelming server
        const delay = index * 200; // 0ms for first, 200ms for second
        
        setTimeout(() => {
          console.log(`🔮 Starting prefetch for conversation ${nextId}`);
          
          // Prefetch in background (non-blocking, low priority)
          AnalyticsApiService.getConversation(convIdNumber)
            .then(fresh => {
              // Store in cache for instant access when clicked
              conversationCache.set(convIdNumber, fresh);
              
              // Also update the conversation in state with full messages
              const transformed = transformBackendConversation(fresh);
              setConversations(prev => prev.map(c => {
                if (c.id !== nextId) return c;
                const merged = mergeMessages(c.messages, transformed.messages);
                const last = merged.length ? merged[merged.length - 1].createdAt : c.lastMessageTime;
                const lastUserMessageTime = transformed.lastUserMessageTime ?? c.lastUserMessageTime;
                return { ...transformed, messages: merged, lastMessageTime: last, lastUserMessageTime };
              }));
              hydratedConversationIds.current.add(nextId);
              console.log(`✅ Prefetched conversation ${nextId} - ready for instant activation`);
            })
            .catch(e => {
              // Prefetch failures are non-critical
              console.debug(`Prefetch failed for ${nextId} (non-critical):`, e);
            });
        }, delay);
      }
    });
  }, [activeConversationId, sortedConversations, transformBackendConversation]);

  // Refresh conversations from start
  const refreshConversations = useCallback(async () => {
    currentPageRef.current = 1;
    loadedConversationIds.current.clear();
    authorCursorRef.current = null;
    eventUserIdsRef.current = [];
    eventUserOffsetRef.current = 0;
    console.log(`🔄 Refreshing conversations with filter: ${sortOption}`);
    await loadConversations(false);
  }, [loadConversations, sortOption]);

  // Load more conversations for infinite scroll
  const loadMoreConversations = useCallback(async () => {
    if (!isLoadingMore && hasMore && !listRefreshInFlight.current) {
      console.log(`📜 Loading more conversations (filter: ${sortOption}) - page:`, currentPageRef.current);
      await loadConversations(true);
    }
  }, [isLoadingMore, hasMore, loadConversations, sortOption]);

  // Infinite scroll hook integration
  const { sentinelRef } = useInfiniteScroll({
    onLoadMore: loadMoreConversations,
    hasMore,
    isLoading: isLoadingMore,
    root: scrollContainerRef.current, // Use scroll container, not viewport
    enabled: !isLoading && conversations.length > 0,
  });

  // TTL cleanup for liveUpdateMessageIds to prevent unbounded growth
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const oneHour = 60 * 60 * 1000;
      const ids = liveUpdateMessageIds.current;
      ids.forEach((ts, id) => {
        if (now - ts > oneHour) ids.delete(id);
      });
    }, 10 * 60 * 1000); // every 10 minutes
    return () => clearInterval(interval);
  }, []);

  // Handle scroll events to show/hide scroll-to-top button
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      // Show button when scrolled down more than 200px
      setShowScrollToTop(scrollContainer.scrollTop > 200);
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top function
  const scrollToTop = useCallback(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, []);

  // Handle conversation activation (click to activate, click again to deactivate)
  const handleConversationClick = useCallback(async (conversationId: string) => {
    const conversationIdNumber = parseInt(conversationId, 10);
    
    // Toggle: if clicking the already-active conversation, deactivate it
    if (activeConversationId === conversationIdNumber) {
      console.log(`Deactivating conversation: ${conversationId}`);
      setActiveConversationId(null);
      return;
    }
    
    console.log(`Activating conversation: ${conversationId}`);

    const shouldIncludeAllHistory = true;
    if (shouldIncludeAllHistory) {
      setFullHistoryConversations(prev => {
        if (prev.has(conversationId)) return prev;
        const next = new Set(prev);
        next.add(conversationId);
        return next;
      });
    }
    
    // Lock the current visual position of this conversation when activating it
    const currentIndex = sortedConversations.findIndex(c => c.id === conversationId);
    if (currentIndex !== -1) {
      lockedActivePositionRef.current = {
        conversationId,
        index: currentIndex
      };
      console.log(`🔒 Locked conversation ${conversationId} at position ${currentIndex}`);
    }
    
    // OPTIMISTIC RENDERING: Activate conversation IMMEDIATELY with existing messages
    setActiveConversationId(conversationIdNumber);
    console.log(`⚡ Conversation ${conversationId} activated instantly (showing existing messages)`);

    // Fetch full message history in BACKGROUND (non-blocking)
    // User sees existing messages immediately while this loads
    const convIdNumber = conversationIdNumber;

    // Check cache first for instant load
    const cached = conversationCache.get(convIdNumber);
    if (cached) {
      console.log(`💾 Loading full conversation ${conversationId} from cache (instant)`);
      const transformed = transformBackendConversation(cached);
      setConversations(prev => prev.map(c => {
        if (c.id !== conversationId) return c;
        const merged = mergeMessages(c.messages, transformed.messages);
        const last = merged.length ? merged[merged.length - 1].createdAt : c.lastMessageTime;
        const lastUserMessageTime = transformed.lastUserMessageTime ?? c.lastUserMessageTime;
        return { ...transformed, messages: merged, lastMessageTime: last, lastUserMessageTime };
      }));
      hydratedConversationIds.current.add(conversationId);
    }

    // Always fetch full history on activation (non-blocking)
    console.log(`🌐 Fetching full conversation ${conversationId} from API (background)`);
    AnalyticsApiService.getConversation(convIdNumber, shouldIncludeAllHistory)
      .then(fresh => {
        // Store in cache for future instant access
        conversationCache.set(convIdNumber, fresh);

        const transformed = transformBackendConversation(fresh);
        setConversations(prev => prev.map(c => {
          if (c.id !== conversationId) return c;
          const merged = mergeMessages(c.messages, transformed.messages);
          const last = merged.length ? merged[merged.length - 1].createdAt : c.lastMessageTime;
          if (import.meta.env.VITE_ENABLE_ANALYTICS === 'true') {
            console.debug('[hydrate] merged', merged.length, 'prev', c.messages.length, 'cid', conversationId);
          }
          const lastUserMessageTime = transformed.lastUserMessageTime ?? c.lastUserMessageTime;
          return { ...transformed, messages: merged, lastMessageTime: last, lastUserMessageTime };
        }));
        hydratedConversationIds.current.add(conversationId);
        console.log(`✅ Background fetch complete for ${conversationId}`);
      })
      .catch(e => {
        console.warn('Background hydrate failed for conversation', conversationId, e);
      });

    // Mark as read in background (non-blocking)
    AnalyticsApiService.markConversationRead(conversationIdNumber)
      .then(rs => {
        const lastReadAt = rs.last_read_at ? new Date(rs.last_read_at) : new Date();
        setConversations(prev => prev.map(c => c.id === conversationId ? { ...c, lastReadAt, unread: false } : c));
      })
      .catch(e => {
        console.warn('Failed to mark conversation read', e);
      });
  }, [sortedConversations, activeConversationId, transformBackendConversation, mergeMessages]);

  // Clear locked position when conversation becomes inactive
  useEffect(() => {
    if (!activeConversationId && lockedActivePositionRef.current) {
      console.log(`🔓 Unlocked conversation ${lockedActivePositionRef.current.conversationId} - allowing re-sort`);
      lockedActivePositionRef.current = null;
    } else if (activeConversationId && lockedActivePositionRef.current && 
               lockedActivePositionRef.current.conversationId !== String(activeConversationId)) {
      // Different conversation became active - clear the old lock
      console.log(`🔓 Unlocked conversation ${lockedActivePositionRef.current.conversationId} - new active conversation`);
      lockedActivePositionRef.current = null;
    }
  }, [activeConversationId]);

  // Handle expand conversation
  const handleExpandConversation = useCallback((conversationId: string) => {
    console.log(`📱 Expanding conversation: ${conversationId}`);
    setExpandedConversationId(conversationId);
  }, []);

  // Handle close expanded modal
  const handleCloseExpanded = useCallback(() => {
    console.log(`📱 Closing expanded conversation`);
    setExpandedConversationId(null);
  }, []);

  // Handle toggle full history
  const handleToggleFullHistory = useCallback(async (conversationId: string, enabled: boolean) => {
    console.log(`🕐 Toggling full history for conversation ${conversationId}: ${enabled}`);
    
    // Update the set of conversations with full history
    setFullHistoryConversations(prev => {
      const newSet = new Set(prev);
      if (enabled) {
        newSet.add(conversationId);
      } else {
        newSet.delete(conversationId);
      }
      return newSet;
    });

    // Re-fetch the conversation with the new history setting
    try {
      const convIdNumber = parseInt(conversationId, 10);
      const fresh = await AnalyticsApiService.getConversation(convIdNumber, enabled);
      const transformed = transformBackendConversation(fresh);
      
      setConversations(prev => prev.map(c => {
        if (c.id !== conversationId) return c;
        // Merge with existing conversation to preserve any local state
        const lastUserMessageTime = transformed.lastUserMessageTime ?? c.lastUserMessageTime;
        return {
          ...c,
          ...transformed,
          lastUserMessageTime,
          messages: transformed.messages,
        };
      }));
      
      // Mark as hydrated with full history
      hydratedConversationIds.current.add(conversationId);
      
      console.log(`✅ Conversation ${conversationId} reloaded with ${enabled ? 'full' : 'limited'} history: ${transformed.messages.length} messages`);
    } catch (error) {
      console.error(`Failed to reload conversation ${conversationId} with full history:`, error);
    }
  }, []);

  return (
    <div className="flex h-full flex-col">
      {/* Tab Bar */}
      <div className="px-8 pt-6 pb-5 bg-gradient-to-b from-white/70 via-white/50 to-transparent dark:from-gray-900/70 dark:via-gray-900/50 dark:to-transparent backdrop-blur-xl sticky top-0 z-20">
        <div className="flex items-center justify-between">
          <ConversationTabBar 
            activeSort={sortOption}
            onSortChange={(newSort) => {
              console.log(`Switching filter from ${sortOption} to ${newSort}`);
              sortOptionRef.current = newSort;
              setSortOption(newSort);
              currentPageRef.current = 1;
              loadedConversationIds.current.clear();
              authorCursorRef.current = null;
              eventUserIdsRef.current = [];
              eventUserOffsetRef.current = 0;
            }}
            onSearchClick={onSearchPageOpen}
            replyRangeDays={replyRangeDays}
            onReplyRangeChange={(range) => {
              replyRangeDaysRef.current = range;
              setReplyRangeDays(range);
            }}
          />
          
          {/* Refresh button */}
          <motion.button
            onClick={refreshConversations}
            disabled={isLoading}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200
              ${isLoading 
                ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed' 
                : 'bg-white/20 dark:bg-gray-800/40 backdrop-blur-xl text-gray-700 dark:text-gray-300 hover:bg-white/30 dark:hover:bg-gray-800/60 active:scale-95'
              }
            `}
            whileHover={!isLoading ? { scale: 1.02 } : {}}
            whileTap={!isLoading ? { scale: 0.98 } : {}}
          >
            <svg 
              className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
              />
            </svg>
            {isLoading ? 'Refreshing...' : 'Refresh'}
          </motion.button>
        </div>
        
        {/* Error banners */}
        {error && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-800">{error}</p>
                </div>
              </div>
              <button
                onClick={() => setError(null)}
                className="text-yellow-600 hover:text-yellow-800 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}
        
        {/* Search banners removed - search is now a separate page */}
      </div>

      {/* Main Content - two-column layout per Figma */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-8 py-8">
          {isLoading ? (
            // Loading state
            <div className="flex items-center justify-center h-64">
              <div className="text-center space-y-4">
                <div className="w-12 h-12 border-4 border-gray-300 dark:border-gray-700 border-t-gray-600 dark:border-t-gray-400 rounded-full animate-spin mx-auto"></div>
                <p className="text-gray-600 dark:text-gray-400 font-['SF_Pro_Text',_-apple-system,_BlinkMacSystemFont,_'Segoe_UI',_Roboto,_'Helvetica_Neue',_Arial,_sans-serif]">
                  Loading conversations from analytics backend...
                </p>
              </div>
            </div>
          ) : sortedConversations.length === 0 ? (
            // Empty state
            <div className="flex items-center justify-center h-64">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto">
                  <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <div className="space-y-2">
                  <h3 className="font-['SF_Pro_Display',_-apple-system,_BlinkMacSystemFont,_'Segoe_UI',_Roboto,_'Helvetica_Neue',_Arial,_sans-serif] font-[590] text-gray-900 dark:text-gray-100 text-lg">
                    No conversations found
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 font-['SF_Pro_Text',_-apple-system,_BlinkMacSystemFont,_'Segoe_UI',_Roboto,_'Helvetica_Neue',_Arial,_sans-serif] max-w-md">
                    No conversations are available from the analytics backend.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Conditional rendering: Posts vs Conversations */}
              <div className="space-y-10">
                {sortedConversations
                  .filter((item: any) => item.name !== undefined)
                  .map((conversation) => (
                    <div key={conversation.id} data-conversation-id={conversation.id}>
                      <IntegratedConversationItem
                        conversation={conversation}
                        onSendMessage={handleSendMessage}
                        onReactMessage={handleReactMessage}
                        onLiveMessage={handleLiveMessage}
                        onConversationClick={handleConversationClick}
                        onExpandClick={handleExpandConversation}
                        onToggleFullHistory={handleToggleFullHistory}
                        isActiveConversation={activeConversationId !== null && activeConversationId === parseInt(conversation.id, 10)}
                        onTypingChange={setIsTyping}
                        onInspectingChange={setIsInspecting}
                        showFullHistory={fullHistoryConversations.has(conversation.id)}
                      />
                    </div>
                  ))}
              </div>
              
              {/* Expanded Conversation Modal */}
              <ExpandedConversationModal
                    isOpen={!!expandedConversationId}
                    onClose={handleCloseExpanded}
                    conversation={sortedConversations.find(c => c.id === expandedConversationId) || null}
                    onSendMessage={handleSendMessage}
                    onReactMessage={handleReactMessage}
                    onLiveMessage={handleLiveMessage}
              />
              
                  {/* Infinite scroll loading indicator and sentinel */}
                  {isLoadingMore && (
                    <div className="flex items-center justify-center py-8">
                      <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-sm font-['SF_Pro_Text',_-apple-system,_BlinkMacSystemFont,_'Segoe_UI',_Roboto,_'Helvetica_Neue',_Arial,_sans-serif]">
                          Loading more...
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {/* Sentinel element for infinite scroll */}
                  <div ref={sentinelRef} className="h-4" />
                  
                  {/* End of list indicator */}
                  {!hasMore && sortedConversations.length > 0 && (
                    <div className="text-sm text-gray-500 dark:text-gray-400 font-['SF_Pro_Text',_-apple-system,_BlinkMacSystemFont,_'Segoe_UI',_Roboto,_'Helvetica_Neue',_Arial,_sans-serif] text-center py-4">
                      {sortedConversations.length} conversations loaded
                    </div>
                  )}
            </div>
          )}
        </div>
      </div>

      {/* Scroll to Top Button */}
      <AnimatePresence>
        {showScrollToTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
            onClick={scrollToTop}
            className="fixed bottom-8 right-8 w-12 h-12 bg-white shadow-lg rounded-full flex items-center justify-center hover:shadow-xl transition-shadow duration-200 z-30"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <svg 
              className="w-6 h-6 text-gray-700" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M5 10l7-7m0 0l7 7m-7-7v18" 
              />
            </svg>
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}

// Analytics Page Component
function AnalyticsPage() {
  return (
    <div className="flex h-full flex-col">
      {/* Content */}
      <div className="flex-1 overflow-y-auto analytics-gradient-light dark:analytics-gradient-dark">
        <div className="max-w-7xl mx-auto px-8 py-12">
          <AllMetricsGrid />
        </div>
      </div>
    </div>
  );
}

// Placeholder Page Component
function PlaceholderPage({ title }: { title: string }) {
  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="text-center space-y-6">
        <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 flex items-center justify-center mx-auto shadow-lg">
          <div className="w-12 h-12 rounded-2xl bg-white/50 dark:bg-gray-700/50 backdrop-blur-xl flex items-center justify-center">
            <div className="w-6 h-6 rounded-lg bg-gray-400 dark:bg-gray-600"></div>
          </div>
        </div>
        <div className="space-y-3">
          <h2 className="font-['SF_Pro_Display',_-apple-system,_BlinkMacSystemFont,_'Segoe_UI',_Roboto,_'Helvetica_Neue',_Arial,_sans-serif] font-[590] text-black dark:text-white text-2xl">
            {title}
          </h2>
          <p className="font-['SF_Pro_Text',_-apple-system,_BlinkMacSystemFont,_'Segoe_UI',_Roboto,_'Helvetica_Neue',_Arial,_sans-serif] font-[400] text-gray-500 dark:text-gray-400 text-lg max-w-md mx-auto leading-relaxed">
            This feature is coming soon. Stay tuned for updates to your Series analytics platform.
          </p>
        </div>
      </div>
    </div>
  );
}

// Authenticated App Component
function AuthenticatedApp() {
  const [currentPage, setCurrentPage] = useState('conversations');
  const [showConnectionTest, setShowConnectionTest] = useState(false);
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Load persisted sidebar state
  useEffect(() => {
    try {
      const stored = localStorage.getItem('series_sidebar_collapsed');
      if (stored === 'true') setIsSidebarCollapsed(true);
    } catch {}
  }, []);

  const toggleSidebar = useCallback(() => {
    setIsSidebarCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('series_sidebar_collapsed', String(next)); } catch {}
      return next;
    });
  }, []);

  // Global message handlers (shared across pages)
  const handleGlobalSendMessage = useCallback(async (conversationId: string, text: string) => {
    const convIdNumber = parseInt(conversationId, 10);
    
    try {
      // Search/other pages don't have conversation state available here; resolve the
      // correct Series user_id (must be a participant) via cache or a lightweight fetch.
      const cached = conversationCache.get(convIdNumber) as any;
      const cachedRecipient = getPrimaryConversationUserId(cached);
      const recipientUserId = cachedRecipient
        ? cachedRecipient
        : getPrimaryConversationUserId(await AnalyticsApiService.getConversation(convIdNumber) as any);

      if (!recipientUserId) {
        throw new Error(`Unable to determine recipient user_id for conversation ${conversationId}`);
      }

      await AnalyticsApiService.sendAsAI(convIdNumber, text, recipientUserId);
      conversationCache.invalidate(convIdNumber);
      console.log(`✅ Message sent to conversation ${conversationId}`);
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  }, []);

  const handleGlobalLiveMessage = useCallback((conversationId: string, messageData: any) => {
    console.log('📨 Global live message received:', conversationId, messageData);
    // Handle global SSE messages if needed
  }, []);

  const renderMainContent = () => {
    switch (currentPage) {
      case 'analytics':
        return <AnalyticsPage />;
      case 'search':
        return (
          <SearchPage 
            onSendMessage={handleGlobalSendMessage}
            onLiveMessage={handleGlobalLiveMessage}
            onClose={() => setCurrentPage('conversations')}
          />
        );
      case 'data-agent':
        return <SeriesDataAgent />;
      case 'enterprise-engagements':
        return <EnterpriseEngagements />;
      case 'flagged-users':
        return <FlaggedUsersPage />;
      case 'segregation':
        return <SegregationPage />;
      case 'user-profiles-feed':
        return <UserProfilesFeedPage />;
      case 'admin-users':
        // Only allow users with user_management permission to access
        if (!canManageUsers(user)) {
          return <PlaceholderPage title="Access Denied" />;
        }
        return <AdminUserManagement />;
      case 'settings':
        return <SettingsPage />;
      default:
        return <ConversationsPageContent onSearchPageOpen={() => setCurrentPage('search')} />;
    }
  };

  // Toggle connection test with Ctrl+Shift+T
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'T') {
        setShowConnectionTest(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="relative size-full min-h-screen animated-gradient-bg-light dark:animated-gradient-bg-dark">
      {/* Sidebar */}
      <motion.div 
        className="fixed h-screen left-0 top-0 z-10"
        initial={{ x: -280 }}
        animate={{ 
          x: 0,
          width: isSidebarCollapsed ? '72px' : '229px' 
        }}
        transition={{ 
          x: { duration: 0.5, ease: 'easeOut' },
          width: { type: 'spring', stiffness: 300, damping: 30, mass: 0.8 }
        }}
      >
        <Sidebar />
        <motion.div 
          className="absolute flex flex-col h-full"
          initial={{ opacity: 0 }}
          animate={{
            opacity: 1,
            inset: isSidebarCollapsed ? '8px' : '16px'
          }}
          transition={{ 
            opacity: { duration: 0.3, delay: 0.2 },
            inset: { type: 'spring', stiffness: 300, damping: 30 }
          }}
        >
          {/* Logo */}
          {/* Header with Hamburger and Logo */}
          <div className="mb-8 pt-4 space-y-4">
            {/* Hamburger Menu Button */}
            <motion.button
              onClick={toggleSidebar}
              aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className={`
                relative group
                flex items-center justify-center 
                ${isSidebarCollapsed ? 'w-full h-10' : 'w-10 h-10'}
                rounded-xl 
                bg-white/25 dark:bg-gray-800/35
                hover:bg-white/40 dark:hover:bg-gray-800/55
                text-gray-700 dark:text-gray-300 
                backdrop-blur-lg 
                border border-white/30 dark:border-gray-700/40
                shadow-sm hover:shadow-md
                transition-all duration-300 ease-out
                overflow-hidden
              `}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              transition={{ type: 'spring', stiffness: 450, damping: 25 }}
            >
              {/* Subtle shine effect on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
              
              <motion.svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5 relative z-10"
                initial={false}
              >
                {/* Animated hamburger lines */}
                <motion.path
                  d="M4 6h16"
                  animate={{ 
                    d: isSidebarCollapsed ? "M6 6h12" : "M4 6h16",
                    opacity: 1
                  }}
                  transition={{ duration: 0.25 }}
                />
                <motion.path
                  d="M4 12h16"
                  animate={{ opacity: isSidebarCollapsed ? 0.6 : 1 }}
                  transition={{ duration: 0.25 }}
                />
                <motion.path
                  d="M4 18h16"
                  animate={{ 
                    d: isSidebarCollapsed ? "M6 18h12" : "M4 18h16",
                    opacity: 1
                  }}
                  transition={{ duration: 0.25 }}
                />
              </motion.svg>
            </motion.button>

            {/* Logo Section */}
            <AnimatePresence mode="wait">
              {isSidebarCollapsed ? (
                <motion.div 
                  key="collapsed-logo"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.2 }}
                  className="text-center"
                >
                  <div className="font-['SF_Pro_Display',_-apple-system,_BlinkMacSystemFont,_'Segoe_UI',_Roboto,_'Helvetica_Neue',_Arial,_sans-serif] font-[700] text-[#000000] dark:text-white text-[32px] leading-tight tracking-tight">
                    S
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="expanded-logo"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                >
                  <div className="font-['SF_Pro_Display',_-apple-system,_BlinkMacSystemFont,_'Segoe_UI',_Roboto,_'Helvetica_Neue',_Arial,_sans-serif] font-[590] text-[#000000] dark:text-white text-[48px] leading-tight tracking-tight">
                    Series
                  </div>
                  <div className="font-['SF_Pro_Display',_-apple-system,_BlinkMacSystemFont,_'Segoe_UI',_Roboto,_'Helvetica_Neue',_Arial,_sans-serif] font-[510] text-[#666666] dark:text-gray-400 text-[16px] tracking-tight mt-0.5">
                    analytics
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Navigation */}
          <div className="mb-8">
            <SidebarNavigation
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              isCollapsed={isSidebarCollapsed}
              user={user}
            />
          </div>
          
          {/* Divider */}
          <div className="mb-4 h-px bg-gradient-to-r from-transparent via-white/20 dark:via-gray-700/40 to-transparent" />

          {/* Dark Mode Toggle */}
          <div className="mb-4">
            <motion.div
              onClick={toggleTheme}
              className={`
                relative overflow-hidden
                box-border content-stretch flex flex-row ${isSidebarCollapsed ? 'gap-0 justify-center p-2.5' : 'gap-3.5 px-3 py-2.5'} items-center 
                rounded-xl cursor-pointer transition-all duration-300 ease-out
                hover:bg-white/12 dark:hover:bg-gray-800/35 hover:shadow-sm 
                border border-transparent hover:border-white/15 dark:hover:border-gray-700/30
              `}
              whileHover={{ scale: 1.015, x: isSidebarCollapsed ? 0 : 2 }}
              whileTap={{ scale: 0.985 }}
              transition={{ type: 'spring', stiffness: 400, damping: 28 }}
            >
              <div className="relative shrink-0 size-6 text-gray-600 dark:text-gray-400 transition-colors duration-200">
                <AnimatePresence mode="wait">
                  {theme === 'light' ? (
                    <motion.div
                      key="moon"
                      initial={{ rotate: -90, opacity: 0 }}
                      animate={{ rotate: 0, opacity: 1 }}
                      exit={{ rotate: 90, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="absolute inset-0"
                    >
                      <THEME_ICONS.Moon className="size-full" />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="sun"
                      initial={{ rotate: -90, opacity: 0 }}
                      animate={{ rotate: 0, opacity: 1 }}
                      exit={{ rotate: 90, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="absolute inset-0"
                    >
                      <THEME_ICONS.Sun className="size-full" />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              
              <AnimatePresence>
                {!isSidebarCollapsed && (
                  <motion.div 
                    className="font-['SF_Pro_Display',_-apple-system,_BlinkMacSystemFont,_'Segoe_UI',_Roboto,_'Helvetica_Neue',_Arial,_sans-serif] font-[590] leading-[0] relative shrink-0 text-[15px] text-nowrap text-gray-600 dark:text-gray-400 transition-colors duration-200"
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: 'auto' }}
                    exit={{ opacity: 0, width: 0 }}
                    transition={{ duration: 0.25 }}
                  >
                    <p className="block leading-[normal] whitespace-pre">{theme === 'light' ? 'Dark Mode' : 'Light Mode'}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>

          {/* User info and logout */}
          <div className="mt-auto mb-4 space-y-3">
            {/* User Info Card */}
            {user && !isSidebarCollapsed && (
              <motion.div 
                className="px-3 py-3 rounded-xl bg-white/10 dark:bg-gray-800/20 backdrop-blur-md border border-white/20 dark:border-gray-700/30 shadow-sm"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                <p className="font-['SF_Pro_Display',_-apple-system,_sans-serif] font-semibold text-sm text-gray-800 dark:text-gray-200 truncate">
                  {user.full_name || user.username}
                </p>
                <p className="font-['SF_Pro_Text',_-apple-system,_sans-serif] text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                  {user.email}
                </p>
              </motion.div>
            )}
            
            {/* Settings Button */}
            <NavigationButton 
              icon={SETTINGS_ITEM.icon} 
              label={SETTINGS_ITEM.label} 
              isActive={currentPage === SETTINGS_ITEM.page}
              onClick={() => setCurrentPage(SETTINGS_ITEM.page)}
              collapsed={isSidebarCollapsed}
            />
            
            {/* Logout Button */}
            <motion.button
              onClick={logout}
              className={`
                relative overflow-hidden group
                w-full box-border content-stretch flex flex-row 
                ${isSidebarCollapsed ? 'gap-0 justify-center p-2.5' : 'gap-3.5 px-3 py-2.5'} 
                items-center rounded-xl cursor-pointer transition-all duration-300 ease-out
                hover:bg-red-50 dark:hover:bg-red-950/30 
                text-red-600 dark:text-red-400
                border border-transparent hover:border-red-200/50 dark:hover:border-red-800/40
                hover:shadow-sm
              `}
              whileHover={{ scale: 1.015, x: isSidebarCollapsed ? 0 : 2 }}
              whileTap={{ scale: 0.985 }}
              transition={{ type: 'spring', stiffness: 400, damping: 28 }}
            >
              <LOGOUT_ICON className="relative shrink-0 transition-colors duration-200" size={24} />
              
              <AnimatePresence>
                {!isSidebarCollapsed && (
                  <motion.div 
                    className="font-['SF_Pro_Display',_-apple-system,_BlinkMacSystemFont,_'Segoe_UI',_Roboto,_'Helvetica_Neue',_Arial,_sans-serif] font-[590] leading-[0] relative shrink-0 text-[15px] text-nowrap"
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: 'auto' }}
                    exit={{ opacity: 0, width: 0 }}
                    transition={{ duration: 0.25 }}
                  >
                    <p className="block leading-[normal] whitespace-pre">Logout</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        </motion.div>
      </motion.div>

      {/* Main Content */}
      <motion.div 
        className="h-screen flex flex-col overflow-hidden"
        initial={{ x: 100, opacity: 0 }}
        animate={{ 
          x: 0,
          opacity: 1,
          marginLeft: isSidebarCollapsed ? '72px' : '229px' 
        }}
        transition={{ 
          x: { duration: 0.5, ease: 'easeOut' },
          opacity: { duration: 0.4, delay: 0.1 },
          marginLeft: { type: 'spring', stiffness: 300, damping: 30, mass: 0.8 }
        }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={currentPage}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="h-full"
          >
            {renderMainContent()}
          </motion.div>
        </AnimatePresence>
      </motion.div>
      
      {/* Backend Connection Test (toggle with Ctrl+Shift+T) */}
      {showConnectionTest && <BackendConnectionTest />}
    </div>
  );
}

// Main App Component with Authentication
function AppContent() {
  const { isAuthenticated, isLoading, login } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen animated-gradient-bg-light dark:animated-gradient-bg-dark flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 border-4 border-gray-300 dark:border-gray-700 border-t-gray-600 dark:border-t-gray-400 rounded-full animate-spin mx-auto"></div>
          <p className="text-gray-600 dark:text-gray-400 font-['SF_Pro_Text',_-apple-system,_BlinkMacSystemFont,_'Segoe_UI',_Roboto,_'Helvetica_Neue',_Arial,_sans-serif]">
            Loading...
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage onLogin={login} />;
  }

  return <AuthenticatedApp />;
}

// Main App Component
export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </ThemeProvider>
  );
}